/**
 * SelectActionsBarbadosPhase - アクション選択フェーズUIコンポーネント
 *
 * FRONTEND.md Step.3 準拠:
 * - Lovable スタイル適用済み
 *
 * @see FRONTEND.md
 * @see src/types/ui-interfaces.ts
 */

import type { SelectActionsPhaseProps } from '../../types/ui-interfaces';
import type { BarbadosActionType } from '../../types';
import { useTranslations } from '../../i18n';

/**
 * Phase 3: アクション選択フェーズUI（Barbados専用）
 *
 * 機能:
 * - 4つのアクションから1つを選択
 * - 使用済みマーカーの表示
 * - 全マーカー使用時のリセット案内
 */
export function SelectActionsBarbadosPhase({
  actionMarkers,
  allMarkersUsed: _allMarkersUsed,
  isMyTurn,
  onSelectAction,
}: SelectActionsPhaseProps) {
  const t = useTranslations();

  // Barbadosのアクション定義（i18n対応）
  const BARBADOS_ACTIONS: {
    type: BarbadosActionType;
    name: string;
    description: string;
  }[] = [
    { type: 'ENGINEER', name: t.selectActions.actions.engineer.name, description: t.selectActions.actions.engineer.description },
    { type: 'LOCOMOTIVE', name: t.selectActions.actions.locomotive.name, description: t.selectActions.actions.locomotive.description },
    { type: 'URBANIZATION', name: t.selectActions.actions.urbanization.name, description: t.selectActions.actions.urbanization.description },
    { type: 'PRODUCTION', name: t.selectActions.actions.production.name, description: t.selectActions.actions.production.description },
  ];

  return (
    <div className="space-y-4">
      {!isMyTurn ? (
        <p className="text-muted-foreground text-sm">{t.common.waiting}</p>
      ) : (
        <>
          {/* アクション選択ボタン */}
          <div className="grid grid-cols-2 gap-1.5">
            {BARBADOS_ACTIONS.map((action) => (
              <button
                key={action.type}
                className="action-card text-left"
                onClick={() => !actionMarkers[action.type] && onSelectAction(action.type)}
                disabled={actionMarkers[action.type]}
                data-action={action.type}
                data-disabled={actionMarkers[action.type]}
              >
                <div className="font-heading text-sm font-semibold text-foreground leading-tight">{action.name}</div>
                <div className="text-[10px] text-muted-foreground leading-tight">{action.description}</div>
                {actionMarkers[action.type] && (
                  <span className="text-[10px] text-destructive ml-1">{t.selectActions.used}</span>
                )}
              </button>
            ))}
          </div>

          {/* ヘルプテキスト */}
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer hover:text-foreground">{t.selectActions.helpTitle}</summary>
            <ul className="mt-2 ml-4 list-disc space-y-1">
              <li>{t.selectActions.helpSelectOne}</li>
              <li>{t.selectActions.helpLocked}</li>
              <li>{t.selectActions.helpReset}</li>
            </ul>
          </details>
        </>
      )}
    </div>
  );
}

export default SelectActionsBarbadosPhase;
